import React, { FC, useState, useMemo } from 'react';
import classNames from 'classnames';
import { Collapse } from '@/pages/aigc/components/Collapse';
import { IAgentData, AGENT_CATEGORY_MAP } from '../../../types';
import ArrowIcon from '@/pages/aigc/assets/svg/agent-home-data.svg';
import WarningIcon from '@/pages/aigc/assets/svg/agent-skills-warning.svg';
import AgentListItem from './AgentListItem';

interface IAgentGroup {
  categoryName: string;
  categoryNameEn: string;
  agents: IAgentData[];
}

interface IAgentGroupListProps {
  /** 分组后的 Agent 数据 */
  groups: IAgentGroup[];
  /** 当前选中的 Agent ID */
  selectedId: string;
  /** 是否英文 */
  isEN: boolean;
  /** 选中 Agent 回调 */
  onSelect: (agent: IAgentData) => void;
  /** 国际化函数 */
  formatMessage: (descriptor: { id: string; }) => string;
}

/** Agent 分组列表组件（带 Collapse 展开/收起动画） */
const AgentGroupList: FC<IAgentGroupListProps> = ({
  groups,
  selectedId,
  isEN,
  onSelect,
  formatMessage,
}) => {
  // 分类展开/收起状态（默认 '通用对话' 收起）
  const defaultCollapsed = useMemo(() => {
    const set = new Set<string>();
    Object.entries(AGENT_CATEGORY_MAP).forEach(([key, config]) => {
      if (config.defaultCollapsed) {
        set.add(key);
      }
    });
    return set;
  }, []);

  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(
    () => new Set(defaultCollapsed)
  );

  /** 切换分类展开/收起 */
  const handleToggleCategory = (categoryName: string) => {
    setCollapsedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryName)) {
        next.delete(categoryName);
      } else {
        next.add(categoryName);
      }
      return next;
    });
  };

  return (
    <>
      {groups.map((group) => {
        const config = AGENT_CATEGORY_MAP[group.categoryName];
        const isCollapsible = config?.collapsible !== false;
        const isCollapsed = isCollapsible && collapsedCategories.has(group.categoryName);
        let displayName = isEN ? group.categoryNameEn : group.categoryName;
        let categoryNote = '';
        if (config) {
          displayName = isEN ? config.labelEn : config.labelZh;
          categoryNote = (isEN ? config.noteEn : config.noteZh) || '';
        }

        const agentList = group.agents.map((agent) => (
          <AgentListItem
            key={agent.agentId}
            agent={agent}
            isSelected={agent.agentId === selectedId}
            isEN={isEN}
            onClick={onSelect}
            formatMessage={formatMessage}
          />
        ));

        return (
          <div key={group.categoryName} className="dba-agent-selector__group">
            {/* 分类标题：仅可折叠分组展示（如“通用对话”）；静态分组不展示标题 */}
            {isCollapsible && (
              <div
                className={classNames('dba-agent-selector__group-header', {
                  'dba-agent-selector__group-header--collapsed': isCollapsed,
                })}
                onClick={() => handleToggleCategory(group.categoryName)}
              >
                <span className="dba-agent-selector__group-label">
                  <span className={classNames('dba-agent-selector__group-arrow', {
                    'dba-agent-selector__group-arrow--collapsed': isCollapsed,
                  })}>
                    <img src={ArrowIcon} alt="" />
                  </span>
                  <span className="dba-agent-selector__group-title dba-agent-selector__group-title--bold">
                    {displayName}
                  </span>
                </span>
              </div>
            )}
            {/* 注释文案独立一行显示（展开时可见） */}
            {categoryNote && !isCollapsed && (
              <div className="dba-agent-selector__group-note">
                <span>{categoryNote}</span>
                <img className="dba-agent-selector__group-note-icon" src={WarningIcon} alt="" />
              </div>
            )}

            {isCollapsible ? (
              /* 可折叠分组：使用 Collapse 实现带动画的展开/收起 */
              <Collapse isOpen={!isCollapsed}>
                {agentList}
              </Collapse>
            ) : (
              /* 不可折叠分组：始终展开 */
              agentList
            )}
          </div>
        );
      })}
    </>
  );
};

export default AgentGroupList;
